import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { Form, Col, Row, Modal } from "react-bootstrap";

// hooks
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import LabelledInput from "../UIComponents/LabelledInput";
import StrokeListSelecter from "../UIComponents/StrokeListSelecter";
import SubmitButton from "../UIComponents/SubmitButton";
import LinkButton from "../UIComponents/LinkButton";
import OnboardingStepHeader from "./OnboardingStepHeader";
import DescribeSmiSchema from "./validations/describeSMISchema";

// api
import { createSmiIncident } from "../../api/smiRequests";

// lib
// NOTE: this file resolves its own patient/caregiver text directly off
// user.userType rather than going through customizeMessages, so the
// right copy always shows even when testing this route directly
// (outside the full registration flow, where user.patientType may not
// be set to "SMI" yet).

// Saves to POST /record-builder/smi-incident (sets registration step2).
const RecentSMI1 = ({ user, setOnboardingPercent }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  // Diagnosis date lives in local state (not react-hook-form) because the
  // modal unmounts its body when closed, which would unregister the input
  // and drop its value.
  const [diagnosisDate, setDiagnosisDate] = useState("");
  const [diagnosisDateError, setDiagnosisDateError] = useState("");

  // Today as YYYY-MM-DD in local time, for the date input's max and
  // the future-date check.
  const todayStr = new Date().toLocaleDateString("en-CA");

  const history = useHistory();
  const location = useLocation();
  const { register, handleSubmit, errors, watch } = useForm({
    resolver: yupResolver(DescribeSmiSchema),
  });

  const formalDiagnosis = watch("formalDiagnosis");

  const diagnosisInfo = [
    {
      category: "diagnosisTypes",
      items: Array.from({ length: 9 }, (_, i) =>
        t(`recentsmi1.diagnosisTypes.options.${i}`)
      ),
    },
  ];

  const { toggleFromSelectedList, addOther, selectedData, initialize } =
    useListSelector({
      setInitialData: setReturnedData,
      initialData: returnedData,
    });

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(30)
      : setOnboardingPercent(20);
    setReturnedData(diagnosisInfo);
    initialize(diagnosisInfo);
    setLoading(false);
  }, [t]);

  // Open the diagnosis checklist the moment "Yes" is selected.
  useEffect(() => {
    if (formalDiagnosis === "yes") {
      setShowDiagnosisModal(true);
    }
  }, [formalDiagnosis]);

  const validateDiagnosisDate = () => {
    if (!diagnosisDate) return t("recentsmi1.diagnosisDate.required");
    if (diagnosisDate > todayStr) return t("recentsmi1.diagnosisDate.invalid");
    return "";
  };

  // "Done" in the diagnosis modal: check the date and types before
  // closing, so the user sees errors right away instead of only on
  // Save & Continue.
  const handleDiagnosisDone = () => {
    const typesMissing =
      !selectedData.diagnosisTypes || selectedData.diagnosisTypes.length === 0;
    const dateError = validateDiagnosisDate();

    setErrorMessage(typesMissing);
    setDiagnosisDateError(dateError);

    if (typesMissing || dateError) return;
    setShowDiagnosisModal(false);
  };

  const onSubmit = async (data) => {
    if (formalDiagnosis === "yes") {
      const typesMissing =
        !selectedData.diagnosisTypes ||
        selectedData.diagnosisTypes.length === 0;
      const dateError = validateDiagnosisDate();

      setErrorMessage(typesMissing);
      setDiagnosisDateError(dateError);

      if (typesMissing || dateError) {
        setShowDiagnosisModal(true);
        return;
      }
    }
    setErrorMessage(false);
    setDiagnosisDateError("");
    setSubmitting(true);

    const isDiagnosed = formalDiagnosis === "yes";
    try {
      await createSmiIncident({
        patientId: patient.patientId,
        data: {
          date: data.date,
          age: data.age,
          describeEvent: data.describeEvent,
          formalDiagnosis: data.formalDiagnosis,
          diagnosisTypes: isDiagnosed ? selectedData.diagnosisTypes : [],
          diagnosisDate: isDiagnosed ? diagnosisDate : null,
        },
      });
    } catch (err) {
      console.error("createSmiIncident", err);
      setSubmitting(false);
      history.push("/oops");
      return;
    }

    setSubmitting(false);
    history.push("/RecentSMI2", {
      fromPatientControl: location.state?.fromPatientControl,
      date: data.date,
    });
  };

  const isCaregiver = user?.userType === "caregiver";

  const getMessage = (key) => {
    const messages = {
      mainHeader: {
        patient: t("recentsmi1.header.patient"),
        caregiver: t("recentsmi1.header.caregiver"),
      },
      sallieText: {
        patient: t("recentsmi1.sallie.patient"),
        caregiver: t("recentsmi1.sallie.caregiver"),
      },
      dateLabel: {
        patient: t("recentsmi1.date.patient"),
        caregiver: t("recentsmi1.date.caregiver"),
      },
      describeEventLabel: {
        patient: t("recentsmi1.describe.label.patient"),
        caregiver: t("recentsmi1.describe.label.caregiver"),
      },
      describeEventExtraInfo: {
        patient: t("recentsmi1.describe.info"),
        caregiver: t("recentsmi1.describe.info"),
      },
      diagnosisConfirmedLabel: {
        patient: t("recentsmi1.diagnosisConfirmed.label.patient"),
        caregiver: t("recentsmi1.diagnosisConfirmed.label.caregiver"),
      },
      diagnosisTypesLabel: {
        patient: t("recentsmi1.diagnosisTypes.label"),
        caregiver: t("recentsmi1.diagnosisTypes.label"),
      },
      diagnosisDateLabel: {
        patient: t("recentsmi1.diagnosisDate.label"),
        caregiver: t("recentsmi1.diagnosisDate.label"),
      },
      diagnosisTypesDone: {
        patient: t("recentsmi1.diagnosisTypes.done"),
        caregiver: t("recentsmi1.diagnosisTypes.done"),
      },
      diagnosisTypesEdit: {
        patient: t("recentsmi1.diagnosisTypes.edit"),
        caregiver: t("recentsmi1.diagnosisTypes.edit"),
      },
      diagnosisTypesNoneSelected: {
        patient: t("recentsmi1.diagnosisTypes.noneSelected"),
        caregiver: t("recentsmi1.diagnosisTypes.noneSelected"),
      },
    };
    return isCaregiver ? messages[key].caregiver : messages[key].patient;
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <div className="d-block">
            <OnboardingStepHeader text={getMessage("mainHeader")} />
            <Sallie text={getMessage("sallieText")} />
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-block">
              <LabelledInput
                autoFocus
                required
                labelAs="h6"
                name="date"
                placeholder="MM/DD/YYYY"
                type="date"
                date-format="mm/dd/yyyy"
                inputRef={register}
                label={getMessage("dateLabel")}
                errors={errors}
              />
              {/* Age shares the date question's label above ("When and at
                  what age..."), so it has no label of its own. */}
              <Form.Group controlId="age">
                <Form.Control
                  name="age"
                  type="number"
                  min="0"
                  max="120"
                  placeholder={t("recentsmi1.age.placeholder")}
                  aria-label={t("recentsmi1.age.placeholder")}
                  ref={register}
                  isInvalid={!!errors.age}
                  style={{ maxWidth: "150px" }}
                />
                {errors.age && (
                  <Form.Control.Feedback type="invalid">
                    {errors.age.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <LabelledInput
                inputAs="textarea"
                required
                labelAs="h6"
                extraInfo={getMessage("describeEventExtraInfo")}
                name="describeEvent"
                type="text"
                inputRef={register}
                label={getMessage("describeEventLabel")}
                errors={errors}
                rows={3}
              />

              <Form.Group as={Row} className="mt-3">
                <Form.Label column>
                  {getMessage("diagnosisConfirmedLabel")}
                  <span className="required">*</span>
                </Form.Label>
                <Col sm={12}>
                  <Row>
                    <Col sm={3}>
                      <Form.Check
                        type="radio"
                        label={t("recentsmi1.diagnosisConfirmed.options.yes")}
                        name="formalDiagnosis"
                        value="yes"
                        id="formalDiagnosisYes"
                        ref={register}
                      />
                    </Col>
                    <Col sm={3}>
                      <Form.Check
                        type="radio"
                        label={t("recentsmi1.diagnosisConfirmed.options.no")}
                        name="formalDiagnosis"
                        value="no"
                        id="formalDiagnosisNo"
                        ref={register}
                      />
                    </Col>
                    <Col sm={3}>
                      <Form.Check
                        type="radio"
                        label={t("recentsmi1.diagnosisConfirmed.options.idk")}
                        name="formalDiagnosis"
                        value="idk"
                        id="formalDiagnosisIdk"
                        ref={register}
                      />
                    </Col>
                  </Row>
                </Col>
                {errors.formalDiagnosis && (
                  <Form.Control.Feedback
                    style={{ display: "block" }}
                    type="invalid"
                  >
                    {errors.formalDiagnosis.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              {formalDiagnosis === "yes" && !showDiagnosisModal && (
                <div className="mt-2">
                  <strong>{getMessage("diagnosisTypesLabel")}:</strong>{" "}
                  {selectedData.diagnosisTypes &&
                  selectedData.diagnosisTypes.length > 0
                    ? selectedData.diagnosisTypes.join(", ")
                    : getMessage("diagnosisTypesNoneSelected")}
                  {diagnosisDate && (
                    <>
                      <br />
                      <strong>{getMessage("diagnosisDateLabel")}</strong>{" "}
                      {diagnosisDate}
                    </>
                  )}{" "}
                  <LinkButton
                    type="button"
                    onClick={() => setShowDiagnosisModal(true)}
                  >
                    {getMessage("diagnosisTypesEdit")}
                  </LinkButton>
                </div>
              )}

              <Modal
                show={showDiagnosisModal}
                onHide={() => setShowDiagnosisModal(false)}
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>
                    {getMessage("diagnosisTypesLabel")}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form.Group controlId="diagnosisDate" className="mb-3">
                    <Form.Label as="h6">
                      {getMessage("diagnosisDateLabel")}
                      <span className="required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      max={todayStr}
                      value={diagnosisDate}
                      isInvalid={!!diagnosisDateError}
                      onChange={(e) => {
                        setDiagnosisDate(e.target.value);
                        setDiagnosisDateError("");
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {diagnosisDateError}
                    </Form.Control.Feedback>
                  </Form.Group>
                  {returnedData.map((itemList, i) => {
                    const { category } = itemList;
                    return (
                      <StrokeListSelecter
                        key={category}
                        category={category}
                        message=""
                        noSuffix
                        list={itemList.items}
                        index={i}
                        selectedList={selectedData[category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        addOther={addOther}
                      />
                    );
                  })}
                </Modal.Body>
                <Modal.Footer>
                  {errorMessage && (
                    <Form.Control.Feedback
                      style={{ display: "block" }}
                      type="invalid"
                      className="mr-auto"
                    >
                      {t("recentsmi1.choose.error")}
                    </Form.Control.Feedback>
                  )}
                  <SubmitButton type="button" onClick={handleDiagnosisDone}>
                    {getMessage("diagnosisTypesDone")}
                  </SubmitButton>
                </Modal.Footer>
              </Modal>

              {errorMessage && (
                <Form.Control.Feedback
                  style={{ display: "block" }}
                  type="invalid"
                >
                  {t("recentsmi1.choose.error")}
                </Form.Control.Feedback>
              )}

              <Row>
                <SubmitButton type="submit" disabled={submitting}>
                  {t("onboarding.saveContinue")} &gt;
                </SubmitButton>
              </Row>
            </div>
          </Form>
        </>
      )}
    </>
  );
};

export default RecentSMI1;